import type { SelfSharedFolderBuf } from "~/classes/generated/response"
import type { UserSidebarIdBuf, UserSidebarItemBuf } from "~/classes/generated/user"

import { InterfacePanelState, InterfaceSidebarState, UserInterfaceSettingsBuf } from "~/classes/generated/interface"
import { UserBuf, UserPropertyBuf, UserSidebarItemIdBuf } from "~/classes/generated/user"

import { parseVector } from "./datagram"
import { addEntityPropertySelectionToBuf, addEntityPropertyTimestampToBuf, addEntityPropertyValueToBuf, asEntityPropertyStatusBuf, parseEntityPropertySchemaValue, parseEntityPropertySelection, parseEntityPropertyStatus, parseEntityPropertyTimestampBuf } from "./entity"
import { parseFile } from "./file"
import { addConversationIdToBuf, addFileIdToBuf, addSearchIdToBuf, addUserIdToBuf, addUserPropertyIdToBuf, parseConversationId, parseFileId, parseSearchId, parseSharedFolderId, parseUserId, parseUserPropertyId, parseUuidBuf } from "./id"
import { parseSearch } from "./search"

export function parseUserProperty(buf: UserPropertyBuf) {
  // For some unknown reason (as of 11-7-2024), when using tauri channels, there is an
  // odd bug where the `valueArray` return null. While we figure that out, here is a kludge
  let value = buf.valueArray()
  if (!value?.length)
    value = new Uint8Array(parseVector(x => buf.value(x), buf.valueLength()))
  const userProperty: UserProperty = {
    userId: parseUserId(buf.userId()!),
    propertyId: parseUserPropertyId(buf.propertyId()!),
    version: buf.version(),
    schema: buf.schema(),
    value: parseEntityPropertySchemaValue(buf.schema(), value),
    timestamp: parseEntityPropertyTimestampBuf(buf.timestamp()!),
    deleted: buf.deleted(),
    status: parseEntityPropertyStatus(buf.status()),
    epochId: buf.epochId() ?? undefined,
  }
  return userProperty
}

export function parseUser(buf: UserBuf): User {
  const userId = parseUserId(buf.userId()!)
  const selection = parseEntityPropertySelection(buf.selection())
  const properties: Record<string, UserProperty> = {}
  for (let i = 0; i < buf.propertiesLength(); i++) {
    const property = parseUserProperty(buf.properties(i)!)
    properties[property.propertyId] = property
  }
  return {
    userId,
    properties,
    selection,
  }
}

export function parseSelfSharedFolder(sharedFolder: SelfSharedFolderBuf) {
  const sharedFolderId = parseSharedFolderId(sharedFolder.sharedFolderId()!)
  const home = parseFile(sharedFolder.home()!)
  const archive = parseFile(sharedFolder.archive()!)
  return { sharedFolderId, home, archive }
}

export function parseUserSidebarItem(buf: UserSidebarItemBuf): UserSidebarItem {
  const fractionalIndex = buf.fractionalIndex()!
  if (buf.fileItem()) {
    return { fractionalIndex, fileItem: parseFile(buf.fileItem()!) }
  }
  else if (buf.searchItem()) {
    return { fractionalIndex, searchItem: parseSearch(buf.searchItem()!) }
  }
  else {
    throw new Error("Cannot parse sidebar item")
  }
}

export function addUserPropertyToBuf(fbb: DatagramBufferBuilder, property: UserProperty) {
  const value = addEntityPropertyValueToBuf(fbb, property.value)
  const userId = addUserIdToBuf(fbb, property.userId)
  const propertyId = addUserPropertyIdToBuf(fbb, property.propertyId)
  const timestamp = addEntityPropertyTimestampToBuf(fbb, property.timestamp)

  UserPropertyBuf.startUserPropertyBuf(fbb)
  UserPropertyBuf.addUserId(fbb, userId)
  UserPropertyBuf.addPropertyId(fbb, propertyId)
  UserPropertyBuf.addVersion(fbb, property.version)
  UserPropertyBuf.addSchema(fbb, property.schema)
  UserPropertyBuf.addValue(fbb, value)
  UserPropertyBuf.addTimestamp(fbb, timestamp)
  if (property.deleted)
    UserPropertyBuf.addDeleted(fbb, property.deleted)
  UserPropertyBuf.addStatus(fbb, asEntityPropertyStatusBuf(property.status))
  if (property.epochId !== undefined)
    UserPropertyBuf.addEpochId(fbb, property.epochId)
  return UserPropertyBuf.endUserPropertyBuf(fbb)
}

export function addUserToBuf(fbb: DatagramBufferBuilder, user: User) {
  const innerProperties = Object.values(user.properties).map(p => addUserPropertyToBuf(fbb, p!))
  const properties = UserBuf.createPropertiesVector(fbb, innerProperties)
  let selection
  if (user.selection)
    selection = addEntityPropertySelectionToBuf(fbb, user.selection)
  const userId = addUserIdToBuf(fbb, user.userId)
  UserBuf.startUserBuf(fbb)
  UserBuf.addUserId(fbb, userId)
  UserBuf.addProperties(fbb, properties)
  if (selection)
    UserBuf.addSelection(fbb, selection)
  return UserBuf.endUserBuf(fbb)
}

export function parseUserSidebarId(buf: UserSidebarIdBuf): string {
  const uuid = parseUuidBuf(buf.id()!)
  return uuid.toString()
}

export function parseUserSidebarItemId(buf: UserSidebarItemIdBuf): UserSidebarItemId {
  const fractionalIndex = buf.fractionalIndex()!
  if (buf.fileItemId())
    return { fractionalIndex, fileItemId: parseFileId(buf.fileItemId()!) }
  else if (buf.searchItemId())
    return { fractionalIndex, searchItemId: parseSearchId(buf.searchItemId()!) }
  else
    throw new Error("No file or search item")
}

export function addUserSidebarItemIdToBuf(fbb: DatagramBufferBuilder, item: UserSidebarItemId) {
  const fractionalIndex = fbb.createString(item.fractionalIndex)
  let fileItemId, searchItemId
  if (item.fileItemId)
    fileItemId = addFileIdToBuf(fbb, item.fileItemId)
  else if (item.searchItemId)
    searchItemId = addSearchIdToBuf(fbb, item.searchItemId)

  UserSidebarItemIdBuf.startUserSidebarItemIdBuf(fbb)
  UserSidebarItemIdBuf.addFractionalIndex(fbb, fractionalIndex)
  if (fileItemId)
    UserSidebarItemIdBuf.addFileItemId(fbb, fileItemId)
  else if (searchItemId)
    UserSidebarItemIdBuf.addSearchItemId(fbb, searchItemId)
  else
    throw new Error("No file or search item")
  return UserSidebarItemIdBuf.endUserSidebarItemIdBuf(fbb)
}

export function defaultUserInterfaceSettings(): UserInterfaceSettings {
  return {
    panelState: InterfacePanelState.Details,
    panelWidth: 270,
    sidebarState: InterfaceSidebarState.Favorites,
    sidebarWidth: 224,
    previewFadeOnDwell: true,
    onboardingComplete: false,
  }
}

export function parseUserInterfaceSettings(buf: UserInterfaceSettingsBuf): UserInterfaceSettings {
  const defaults = defaultUserInterfaceSettings()
  return {
    panelState: buf.panelState() ?? defaults.panelState,
    panelWidth: buf.panelWidth() ?? defaults.panelWidth,
    sidebarState: buf.sidebarState() ?? defaults.sidebarState,
    sidebarWidth: buf.sidebarWidth() ?? defaults.sidebarWidth,
    previewFadeOnDwell: buf.previewFadeOnDwell() ?? defaults.previewFadeOnDwell,
    latestConversationId: maybe(parseConversationId)(buf.latestConversationId()),
    onboardingComplete: buf.onboardingComplete(),
  }
}

export function addUserInterfaceSettingsToBuf(fbb: DatagramBufferBuilder, settings: UserInterfaceSettings) {
  let latestConversationId
  if (settings.latestConversationId)
    latestConversationId = addConversationIdToBuf(fbb, settings.latestConversationId)

  UserInterfaceSettingsBuf.startUserInterfaceSettingsBuf(fbb)
  UserInterfaceSettingsBuf.addPanelState(fbb, settings.panelState)
  UserInterfaceSettingsBuf.addPanelWidth(fbb, settings.panelWidth)
  UserInterfaceSettingsBuf.addSidebarState(fbb, settings.sidebarState)
  UserInterfaceSettingsBuf.addSidebarWidth(fbb, settings.sidebarWidth)
  UserInterfaceSettingsBuf.addOnboardingComplete(fbb, settings.onboardingComplete)
  UserInterfaceSettingsBuf.addPreviewFadeOnDwell(fbb, settings.previewFadeOnDwell)
  if (latestConversationId)
    UserInterfaceSettingsBuf.addLatestConversationId(fbb, latestConversationId)
  return UserInterfaceSettingsBuf.endUserInterfaceSettingsBuf(fbb)
}
